import { Accordion, Alert, Button, Spinner } from "flowbite-react";
import Input from "../inputs/Input";
import PhoneInput from "../inputs/PhoneInput";
import CPFInput from "../inputs/CPFInput";
import CNPJInput from "../inputs/CNPJInput";
import CurrencyInput from "../inputs/CurrencyInput";
import SelectInput from "../inputs/SelectInput";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState, useRef } from "react";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import axiosInstance from "axios";
import { ADMIN, CATEGORIES, USERS } from "../../config/apiConfig";
import useCEP from "../../hooks/useCEP";
import { formatToReal, isValidCNPJ, isValidCPF } from "../../utils/utils";
import { FaCheckCircle, FaRegQuestionCircle } from "react-icons/fa";
import moment from "moment";
import { Switch } from "@mui/material";
import { Tooltip } from "react-tooltip";
import { useApplication } from "../../contexts/ApplicationContext";
import { useNavigate } from "react-router-dom";

/* ===========================
 * Helpers
 * =========================== */

const mergeHandlers =
  (...fns) =>
  (e) =>
    fns.filter(Boolean).forEach((fn) => fn(e));

const isRep = (user) =>
  String(user?.Company?.companyTypeId || "")
    .toUpperCase()
    .includes("REPRESENT");

const getBasePrice = (query, tableQueries) =>
  tableQueries?.find((tq) => tq.QueryId === query.id)?.price ?? query.price;

// força string com 2 casas (para o CurrencyInput)
const toFixedStr = (n) =>
  n == null || n === "" || isNaN(Number(n)) ? "" : Number(n).toFixed(2);

const StepHeader = ({ activeStep }) => (
  <ol className="flex items-center w-full text-sm font-medium text-gray-500 sm:text-base">
    {["Cliente", "Usuários", "Permissões", "Confirmação"].map((label, idx) => {
      const step = idx + 1;
      const active = activeStep >= step;
      return (
        <li
          key={label}
          className="flex md:w-full items-center sm:after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-200 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10"
        >
          <span className={`flex items-center ${active ? "text-primary" : ""}`}>
            <span
              className="inline-flex items-center justify-center w-6 h-6 rounded-full border mr-2"
              style={{
                borderColor: "var(--primary)",
                color: active ? "var(--primary)" : "#6b7280",
              }}
            >
              {active ? "✓" : step}
            </span>
            <span className="hidden md:block">{label}</span>
          </span>
        </li>
      );
    })}
  </ol>
);

const SectionCard = ({ title, subtitle, children, right }) => (
  <div className="rounded-2xl border bg-white shadow-sm p-4">
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const FormRow = ({ children, className = "" }) => (
  <div className={`grid grid-cols-1 gap-4 ${className}`}>{children}</div>
);

/* ===========================
 * QueryRow (Permissões)
 * =========================== */
const QueryRow = ({
  idxCat,
  index,
  query,
  register,
  control,
  basePrice,
  isRepresentative,
}) => {
  const validIdx = Number.isInteger(idxCat) && Number.isInteger(index);
  if (!validIdx) return null;

  const inputName = `categories[${idxCat}].queries[${index}]`;
  const id = `q-${idxCat}-${index}`;

  return (
    <div className="rounded-xl border bg-white py-3 px-3 flex flex-col gap-2 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <label htmlFor={id} className="flex items-center cursor-pointer">
            <Controller
              name={`${inputName}.isActive`}
              control={control}
              render={({ field }) => (
                <Switch
                  {...field}
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  color="success"
                  size="medium"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "var(--primary)",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "var(--primary)",
                    },
                  }}
                  id={id}
                />
              )}
            />
            <span className="text-sm font-medium text-gray-800">
              {query.name}
            </span>
          </label>

          {isRepresentative && (
            <span className="text-xs text-gray-500 ml-3">
              Preço de custo: {formatToReal(basePrice ?? query.price)}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* CurrencyInput usa Controller por dentro e já grava "1.23" */}
          <CurrencyInput
            register={register}
            name={`${inputName}.price`}
            className="w-32"
            label="Preço"
            control={control}
          />
          <Input
            type="hidden"
            register={register}
            name={`${inputName}.QueryId`}
          />
        </div>
      </div>
    </div>
  );
};

/* ===========================
 * Componente principal
 * =========================== */
const AdminCustomerForm = () => {
  const navigate = useNavigate();
  const { user } = useApplication();
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorQuery, setErrorQuery] = useState(false);

  const today = moment();
  const creationDay = today.clone().subtract(1, "day").date();
  const expirationDay = today.clone().subtract(1, "day").add(10, "days").date();
  const formatDay = (d) => d.toString().padStart(2, "0");

  // OpenCNPJ
  const [openCnpjLoading, setOpenCnpjLoading] = useState(false);
  const [openCnpjError, setOpenCnpjError] = useState(null);
  const openCnpjDebounceRef = useRef(null);
  const lastFetchedCnpjRef = useRef("");

  // OpenCNPJ: fetch + preenchimento
  async function fetchAndFillFromOpenCNPJ(rawCnpj, overwrite = true) {
    const cnpj = String(rawCnpj || "").replace(/\D/g, "");
    if (!cnpj || cnpj.length !== 14) return;

    setOpenCnpjLoading(true);
    setOpenCnpjError(null);

    try {
      const { data: o } = await axiosInstance.get(
        `https://api.opencnpj.org/${cnpj}`
      );

      // libera autofills
      userEdited.current.address = false;
      userEdited.current.addressNeighborhood = false;
      userEdited.current.addressCity = false;

      setValue("company.socialReason", o.razao_social || "");
      if (o.email) {
        setValue("company.email", o.email);
        clearErrors("company.email");
      }

      // telefone
      let phone = "";
      try {
        if (Array.isArray(o.telefones) && o.telefones.length) {
          const t = o.telefones[0];
          const ddd = String(t.ddd || "").replace(/\D/g, "");
          const num = String(t.numero || "").replace(/\D/g, "");
          phone = ddd && num ? `(${ddd}) ${num}` : "";
        }
      } catch {}
      if (phone) {
        setValue("company.phoneNumber", phone);
        clearErrors("company.phoneNumber");
      }

      // endereço
      if (o.cep) setValue("company.cep", o.cep);
      if (o.logradouro) setValue("company.address", o.logradouro);
      if (o.numero) setValue("company.addressNumber", o.numero);
      if (o.complemento) setValue("company.addressComplement", o.complemento);
      if (o.bairro) setValue("company.addressNeighborhood", o.bairro);
      if (o.municipio) setValue("company.addressCity", o.municipio);

      lastFetchedCnpjRef.current = cnpj;

      try {
        const firstSocio =
          Array.isArray(o.QSA) && o.QSA.length ? o.QSA[0] : null;
        if (firstSocio?.nome_socio) {
          setValue("company.responsibleName", firstSocio.nome_socio);
          clearErrors("company.responsibleName");
        }
      } catch {}
    } catch (err) {
      setOpenCnpjError("Não foi possível buscar dados no OpenCNPJ.");
    } finally {
      setOpenCnpjLoading(false);
    }
  }

  const defaultValues = {
    company: {
      typeCustomer: "CPF",
      typePayment: "PRE",
      generateAutomaticPayment: 1,
      paymentCreationDay: formatDay(creationDay),
      paymentExpirationDay: formatDay(expirationDay),
      priceSurvey: "10.00",
      priceCV: "10.00",
      // novos campos
      limitUse: "", // opcional: deixe vazio ou "0.00"
      dateEndContract: moment().add(1, "year").format("YYYY-MM-DD"),
    },
  };

  const {
    register,
    getValues,
    handleSubmit,
    setValue,
    setError,
    watch,
    control,
    formState: { errors },
    clearErrors,
    reset,
    getFieldState,
    trigger,
  } = useForm({ defaultValues });

  const { setCep, address, loading: loadingCEP } = useCEP();

  /* ===========================
   * Data: tabelas & categorias
   * =========================== */
  const { data: tables } = useQuery(["tables"], async () => {
    const { data } = await axios.get(`${ADMIN.GET_TABLES}?itemsPerPage=-1`);
    return data.data.data;
  });

  const { data: categories } = useQuery(["categories"], async () => {
    const { data } = await axios.get(`${CATEGORIES.GET_ALL}`);
    // pré-popula ids e preços padrão (string com 2 casas)
    data.data.data?.forEach((category, catIdx) => {
      category.Queries?.forEach((query, queryIdx) => {
        setValue(
          `categories[${catIdx}].queries[${queryIdx}].QueryId`,
          query.id,
          { shouldDirty: false }
        );
        setValue(
          `categories[${catIdx}].queries[${queryIdx}].price`,
          toFixedStr(query.price),
          { shouldDirty: false }
        );
      });
    });
    return data.data.data;
  });

  const { data: tableQueries } = useQuery(
    ["tableQueries", watch("company.tableId")],
    async () => {
      if (watch("company.tableId")) {
        const { data: items } = await axios.get(
          `${ADMIN.GET_TABLE_QUERIES}/${watch("company.tableId")}`
        );
        return items.data;
      }
      return null;
    },
    { enabled: !!watch("company.tableId") }
  );

  const { data: usersList, isLoading: loadingUsers } = useQuery(
    ["usersList"],
    async () => {
      const { data } = await axios.get(`${USERS.GET_USERS}?itemsPerPage=-1`);
      return data.data?.data || data.data || [];
    }
  );

  // pré-seleciona usuário logado como responsável, se vazio
  useEffect(() => {
    if (usersList?.length && !watch("company.responsibleId") && user?.id) {
      setValue("company.responsibleId", user.id);
    }
  }, [usersList, user, watch, setValue]);

  // herda vcm/flags/valores da tabela
  useEffect(() => {
    const t = tables?.find((tb) => tb.id == watch("company.tableId"));
    setValue("company.vcm", t?.vcm || "", { shouldDirty: false });
    setValue("company.hasSurvey", t?.hasSurvey || "", { shouldDirty: false });
    setValue("company.hasCV", t?.hasCV || "", { shouldDirty: false });
    setValue("company.priceSurvey", toFixedStr(t?.priceSurvey ?? "10.00"), {
      shouldDirty: false,
    });
    setValue("company.priceCV", toFixedStr(t?.priceCV ?? "10.00"), {
      shouldDirty: false,
    });
  }, [watch("company.tableId"), tables, setValue]);

  // aplica preços/ativação das queries pela tabela (sem sobrescrever se usuário já editou)
  useEffect(() => {
    if (!categories || !tableQueries) return;
    categories.forEach((category, catIdx) => {
      category?.Queries?.forEach((query, index) => {
        const basePath = `categories[${catIdx}].queries[${index}]`;
        setValue(`${basePath}.QueryId`, query.id, { shouldDirty: false });
        const tq = tableQueries.find((cq) => cq.QueryId === query.id);
        if (tq) {
          setValue(`${basePath}.isActive`, true, { shouldDirty: false });
          const { isDirty } = getFieldState(`${basePath}.price`);
          if (!isDirty) {
            setValue(`${basePath}.price`, toFixedStr(tq.price), {
              shouldDirty: false,
            });
          }
        }
      });
    });
  }, [categories, tableQueries, setValue, getFieldState]);

  /* ===========================
   * CEP autofill (sem sobrescrever edição)
   * =========================== */
  const userEdited = useRef({
    address: false,
    addressNeighborhood: false,
    addressCity: false,
  });

  const onBlurSearchCEP = () => {
    setCep(getValues("company.cep"));
  };

  useEffect(() => {
    if (!address) return;
    const { logradouro, bairro, localidade } = address || {};

    if (logradouro && !userEdited.current.address) {
      setValue("company.address", logradouro, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
    if (bairro && !userEdited.current.addressNeighborhood) {
      setValue("company.addressNeighborhood", bairro, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
    if (localidade && !userEdited.current.addressCity) {
      setValue("company.addressCity", localidade, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [address, setValue]);

  /* ===========================
   * Dedupe
   * =========================== */
  const onBlurInfo = async (e, type) => {
    try {
      await axios.get(
        `${ADMIN.GET_COMPANY_BY_INFO}/${e.target.value}?type=${type}`
      );
      setError(`${e.target.name}`, {
        type: "manual",
        message: "Já possui este campo cadastrado!",
      });
    } catch {
      clearErrors(e.target.name);
    }
  };

  const onBlurEmailInfo = async (e) => {
    try {
      await axios.get(`${USERS.GET_USER_BY_EMAIL}/${e.target.value}`);
      clearErrors(e.target.name);
    } catch {
      setError(`${e.target.name}`, {
        type: "manual",
        message: "Já possui este campo cadastrado!",
      });
    }
  };

  /* ===========================
   * Validação CPF/CNPJ
   * =========================== */
  const checkDocument = (e, type) => {
    const unmasked = e.target.value.replace(/\D/g, "");
    const isValid =
      unmasked.length === 11
        ? isValidCPF(unmasked)
        : unmasked.length === 14
        ? isValidCNPJ(unmasked)
        : false;

    if (!isValid) {
      setError(`company.${type}`, {
        type: "manual",
        message: "Documento inválido",
      });
    } else {
      clearErrors(`company.${type}`);
      onBlurInfo(e, type);

      const isCompany = watch("company.typeCustomer") !== "CPF";
      if (isCompany && unmasked.length === 14) {
        fetchAndFillFromOpenCNPJ(unmasked);
      }
    }
  };

  /* ===========================
   * Etapas
   * =========================== */
  const handleStep1Submit = () => setActiveStep(2);
  const handleStep2Submit = () => setActiveStep(3);

  const handleSelectAll = () => {
    setValue("company.hasSurvey", true);
    setValue("company.hasCV", true);
    categories?.forEach((category, catIndex) =>
      category.Queries?.forEach((_, index) =>
        setValue(`categories[${catIndex}].queries[${index}].isActive`, true)
      )
    );
  };

  // Apenas para manter window.form.queries coerente quando avança p/ revisão
  const handleQueriesData = () => {
    handleSubmit((data) => {
      const queries = [];
      data.categories?.forEach((cat) =>
        cat.queries?.forEach((q) => {
          if (q.isActive) {
            queries.push({
              QueryId: q.QueryId,
              price: q.price === "" || q.price == null ? null : Number(q.price),
              isActive: true,
            });
          }
        })
      );
      window.form = { ...window.form, queries };
      setActiveStep(4);
    })();
  };

  const handleFinishSubmit = async (data) => {
    setIsSubmitting(true);
    setErrorQuery(false);
    try {
      // Reconstrói data.queries (o back usa esse array)
      const flat = [];
      data.categories?.forEach((cat) =>
        cat.queries?.forEach((q) => flat.push(q))
      );
      data.queries = flat
        .filter((q) => q.isActive)
        .map((q) => ({
          QueryId: q.QueryId,
          price: q.price === "" || q.price == null ? null : Number(q.price),
          isActive: true,
        }));

      // Converte também company.priceCV / priceSurvey
      if (data.company) {
        if (typeof data.company.priceCV !== "undefined") {
          data.company.priceCV =
            data.company.priceCV === "" || data.company.priceCV == null
              ? null
              : Number(data.company.priceCV);
        }
        if (typeof data.company.priceSurvey !== "undefined") {
          data.company.priceSurvey =
            data.company.priceSurvey === "" || data.company.priceSurvey == null
              ? null
              : Number(data.company.priceSurvey);
        }
        // normaliza limitUse para número se vier preenchido
        if (typeof data.company.limitUse !== "undefined") {
          const v = data.company.limitUse;
          data.company.limitUse =
            v === "" || v == null ? null : Number(String(v).replace(",", "."));
        }
      }

      // DEBUG opcional:
      // console.log("PAYLOAD ENVIADO:", JSON.stringify(data, null, 2));

      await axios.post(ADMIN.NEW_CUSTOMER, data);
      setActiveStep(5);
    } catch (error) {
      setErrorQuery(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ===========================
   * UI derivado
   * =========================== */
  const isCPF = watch("company.typeCustomer") === "CPF";
  const isCompany = !isCPF;

  const goToStep2 = async () => {
    // escolha os campos “essenciais” da etapa 1
    const essentials = isCPF
      ? [
          "company.typeCustomer",
          "company.name",
          "company.document",
          "company.email",
          "company.phoneNumber",
          "company.typePayment",
          "company.cep",
          "company.addressCity",
          "company.addressNeighborhood",
          "company.addressNumber",
          "company.responsibleId",
        ]
      : [
          "company.typeCustomer",
          "company.socialReason",
          "company.document",
          "company.responsibleName",
          "company.responsibleCPF",
          "company.position",
          "company.email",
          "company.phoneNumber",
          "company.typePayment",
          "company.cep",
          "company.addressCity",
          "company.addressNeighborhood",
          "company.addressNumber",
          "company.responsibleId",
        ];

    // se quiser, deixe “Endereço” fora para não travar por causa do CEP
    // essentials.push("company.address");

    const ok = await trigger(essentials, { shouldFocus: true });
    if (ok) setActiveStep(2);
  };

  useEffect(() => {
    if (isCPF) {
      setValue("company.hasCV", false, { shouldValidate: true });
      setValue("company.priceCV", "", { shouldValidate: true });
    }
  }, [isCPF, setValue]);

  // Auto-busca OpenCNPJ (debounce)
  useEffect(() => {
    const isCompanyFlag = watch("company.typeCustomer") !== "CPF";
    const raw = watch("company.document");
    const cnpj = String(raw || "").replace(/\D/g, "");

    if (openCnpjDebounceRef.current) {
      clearTimeout(openCnpjDebounceRef.current);
    }

    if (!isCompanyFlag || cnpj.length !== 14) return;

    openCnpjDebounceRef.current = setTimeout(() => {
      if (lastFetchedCnpjRef.current !== cnpj) {
        if (isValidCNPJ(cnpj)) {
          fetchAndFillFromOpenCNPJ(cnpj, true);
        }
      }
    }, 700);

    return () => {
      if (openCnpjDebounceRef.current) {
        clearTimeout(openCnpjDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("company.document"), watch("company.typeCustomer")]);

  return (
    <div className="bg-white rounded-xl p-2 md:p-5 space-y-6">
      <StepHeader activeStep={activeStep} />

      {/* STEP 1: Cliente */}
      {activeStep === 1 && (
        <form onSubmit={handleSubmit(handleStep1Submit)} className="space-y-6">
          <SectionCard
            title="Dados do cliente"
            subtitle="Preencha as informações cadastrais e de contato."
            right={
              <div className="flex items-center gap-4">
                {loadingCEP && (
                  <span className="inline-flex items-center gap-2 text-xs text-gray-500">
                    <Spinner size="xs" /> Buscando endereço pelo CEP…
                  </span>
                )}
                {openCnpjLoading && (
                  <span className="inline-flex items-center gap-2 text-xs text-gray-500">
                    <Spinner size="xs" /> Buscando dados na receita federal...
                  </span>
                )}
                {openCnpjError && (
                  <span className="text-xs text-red-600">{openCnpjError}</span>
                )}
              </div>
            }
          >
            <FormRow className="lg:grid-cols-3">
              <SelectInput
                register={register}
                label="Tipo de cliente"
                name="company.typeCustomer"
              >
                <option value="CPF">Pessoa Física</option>
                <option value="CNPJ">Pessoa Jurídica</option>
              </SelectInput>

              {isCPF ? (
                <>
                  <div>
                    <Input
                      register={register}
                      name="company.name"
                      label="Nome completo"
                      required
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <Controller
                      name="company.document"
                      control={control}
                      defaultValue=""
                      rules={{ required: "Este campo é obrigatório" }}
                      render={({ field, fieldState }) => (
                        <CPFInput
                          name="company.document"
                          label="CPF"
                          required
                          {...field}
                          value={field.value ?? ""}
                          onBlur={mergeHandlers(field.onBlur, (e) =>
                            checkDocument(e, "document")
                          )}
                          error={fieldState.error?.message}
                        />
                      )}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Input
                      register={register}
                      name="company.socialReason"
                      label="Razão Social"
                      required
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <Controller
                      name="company.document"
                      control={control}
                      defaultValue=""
                      rules={{ required: "Este campo é obrigatório" }}
                      render={({ field, fieldState }) => (
                        <CNPJInput
                          name="company.document"
                          label="CNPJ"
                          required
                          {...field}
                          value={field.value ?? ""}
                          onBlur={mergeHandlers(field.onBlur, (e) =>
                            checkDocument(e, "document")
                          )}
                          error={fieldState.error?.message}
                        />
                      )}
                    />
                  </div>
                </>
              )}
            </FormRow>

            {!isCPF && (
              <FormRow className="lg:grid-cols-3">
                <div>
                  <Input
                    register={register}
                    name="company.responsibleName"
                    label="Nome do responsável"
                    required
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Controller
                    name="company.responsibleCPF"
                    control={control}
                    defaultValue=""
                    rules={{ required: "Este campo é obrigatório" }}
                    render={({ field, fieldState }) => (
                      <CPFInput
                        name="company.responsibleCPF"
                        label="CPF do responsável"
                        required
                        {...field}
                        value={field.value ?? ""}
                        onBlur={mergeHandlers(field.onBlur, (e) =>
                          checkDocument(e, "responsibleCPF")
                        )}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                </div>

                <SelectInput
                  register={register}
                  label="Cargo"
                  name="company.position"
                >
                  <option value="SOCIO">Sócio/Administrador</option>
                  <option value="GERENTE">Gerente</option>
                  <option value="ADM">Administrador</option>
                </SelectInput>
              </FormRow>
            )}

            <FormRow className="lg:grid-cols-3">
              <div>
                <Input
                  type="email"
                  register={register}
                  name="company.email"
                  label="E-mail *"
                  required
                  autoComplete="off"
                  onBlur={(e) => onBlurInfo(e, "email")}
                />
                {errors.company?.email && (
                  <small className="text-xs text-red-500">
                    {errors.company.email.message}
                  </small>
                )}
              </div>
              <div>
                <Controller
                  name="company.phoneNumber"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Este campo é obrigatório" }}
                  render={({ field, fieldState }) => (
                    <PhoneInput
                      name="company.phoneNumber"
                      label="Celular *"
                      required
                      {...field}
                      value={field.value ?? ""}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
              <SelectInput
                register={register}
                label="Tipo de pagamento"
                name="company.typePayment"
              >
                <option value="PRE">Pré-pago</option>
                <option value="POS">Pós-pago</option>
              </SelectInput>
            </FormRow>

            <div>
              <div className="font-semibold pt-3">Endereço do cliente</div>
              <div className="text-gray-500 text-sm mt-1">
                Preencha o endereço do cliente
              </div>
            </div>
            <FormRow className="lg:grid-cols-3">
              <Input
                register={register}
                name="company.cep"
                label="CEP"
                required
                onBlur={onBlurSearchCEP}
              />
              <Input
                register={register}
                name="company.address"
                label="Endereço"
                required
                onChange={() => (userEdited.current.address = true)}
              />
              <Input
                type="text"
                inputMode="numeric"
                pattern="\d*"
                register={register}
                name="company.addressNumber"
                label="Número"
                required
              />
            </FormRow>

            <FormRow className="lg:grid-cols-3">
              <Input
                register={register}
                name="company.addressComplement"
                label="Complemento"
              />
              <Input
                register={register}
                name="company.addressNeighborhood"
                label="Bairro"
                required
                onChange={() => (userEdited.current.addressNeighborhood = true)}
              />
              <Input
                register={register}
                name="company.addressCity"
                label="Cidade"
                required
                onChange={() => (userEdited.current.addressCity = true)}
              />
            </FormRow>

            <div>
              <div className="font-semibold pt-3">Vendedor/Responsável</div>
              <div className="text-gray-500 text-sm mt-1">
                Usuário Responsável/Vendedor deste cliente
              </div>
            </div>
            <FormRow className="lg:grid-cols-3">
              <SelectInput
                register={register}
                label="Usuário responsável (vendedor)"
                name="company.responsibleId"
              >
                <option value="">
                  {loadingUsers ? "Carregando usuários..." : "Selecione"}
                </option>
                {usersList?.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} {u.email ? `— ${u.email}` : ""}
                  </option>
                ))}
              </SelectInput>
            </FormRow>

            {/* NOVA SEÇÃO: Configurações de segurança */}
            <div>
              <div className="font-semibold pt-3">
                Configurações de segurança
              </div>
              <div className="text-gray-500 text-sm mt-1">
                Defina limites de uso mensal e a data de término do contrato
                deste cliente.
              </div>
            </div>
            <FormRow className="lg:grid-cols-3">
              <div className="">
                <CurrencyInput
                  register={register}
                  name="company.limitUse"
                  label="Valor máximo de uso (R$)"
                  placeholder="Ex.: R$ 1.000,00"
                  className="w-full"
                  control={control}
                />
              </div>
              <Input
                type="date"
                register={register}
                name="company.dateEndContract"
                label="Término do contrato"
              />
            </FormRow>
          </SectionCard>

          <div className="flex justify-center">
            <Button
              type="button"
              onClick={goToStep2}
              className="shadow md:max-w-60 px-10"
            >
              Próximo
            </Button>
          </div>
        </form>
      )}

      {/* STEP 2: Usuário */}
      {activeStep === 2 && (
        <form onSubmit={handleSubmit(handleStep2Submit)} className="space-y-6">
          <SectionCard
            title="Usuário administrador"
            subtitle="Crie o primeiro usuário para acessar o painel."
            right={
              <div className="flex items-center gap-2">
                <Button
                  size="xs"
                  type="button"
                  onClick={() => {
                    const email = watch("company.email");
                    const name =
                      watch("company.responsibleName") || watch("company.name");
                    const phone = watch("company.phoneNumber");
                    if (email) setValue("customer.email", email);
                    if (name) setValue("customer.name", name);
                    if (phone) setValue("customer.phoneNumber", phone);
                  }}
                >
                  Reutilizar dados do cliente
                </Button>
                <Tooltip
                  id="tt-reuse"
                  content="Preenche nome, e-mail e celular com os dados da etapa anterior."
                />
                <FaRegQuestionCircle
                  data-tooltip-id="tt-reuse"
                  className="text-gray-500"
                />
              </div>
            }
          >
            <FormRow className="lg:grid-cols-2">
              <Input
                register={register}
                name="customer.name"
                label="Nome"
                required
                autoComplete="off"
              />
              <div>
                <Input
                  type="email"
                  register={register}
                  name="customer.email"
                  label="E-mail"
                  required
                  autoComplete="off"
                  onBlur={onBlurEmailInfo}
                />
                {errors.customer?.email && (
                  <small className="text-xs text-red-500">
                    {errors.customer.email.message}
                  </small>
                )}
              </div>
            </FormRow>

            <FormRow className="lg:grid-cols-2">
              <div>
                <Controller
                  name="customer.phoneNumber"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Este campo é obrigatório" }}
                  render={({ field, fieldState }) => (
                    <PhoneInput
                      name="customer.phoneNumber"
                      label="Celular"
                      required
                      {...field}
                      value={field.value ?? ""}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
              <div>
                <Input
                  type="password"
                  register={register}
                  name="customer.password"
                  label="Senha"
                  autoComplete="off"
                />
                <small className="text-xs text-gray-500">
                  Se vazio, usaremos a senha padrão: 123456
                </small>
              </div>
            </FormRow>
          </SectionCard>

          <div className="flex justify-center gap-2">
            <Button
              onClick={() => setActiveStep(1)}
              color="gray"
              className="md:max-w-60"
            >
              Voltar
            </Button>
            <Button type="submit" className="md:max-w-60">
              Próximo
            </Button>
          </div>
        </form>
      )}

      {/* STEP 3: Permissões */}
      {activeStep === 3 && (
        <form className="space-y-6">
          <SectionCard
            title="Permissões"
            subtitle="Selecione as consultas e (opcionalmente) aplique uma tabela pré-definida."
            right={
              <Button size="xs" onClick={() => handleSelectAll()}>
                Selecionar todos
              </Button>
            }
          >
            <div className="w-full md:w-96">
              <SelectInput
                register={register}
                label="Tabela pré-definida"
                name="company.tableId"
              >
                <option value="">Nenhuma selecionada</option>
                {tables?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} — {t.vcm ? formatToReal(t.vcm) : "Sem VCM"}
                  </option>
                ))}
              </SelectInput>
              {!!watch("company.tableId") && (
                <div className="text-xs text-gray-500 mt-1">
                  VCM:{" "}
                  {watch("company.vcm")
                    ? formatToReal(watch("company.vcm"))
                    : "—"}{" "}
                  • Vistoria: {watch("company.hasSurvey") ? "Sim" : "Não"} • CV:{" "}
                  {watch("company.hasCV") ? "Sim" : "Não"}
                </div>
              )}
            </div>

            {/* CV */}
            {isCompany && (
              <div className="rounded-xl border bg-white p-3 md:p-4">
                <div className="flex items-start justify-between gap-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Controller
                      name="company.hasCV"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          {...field}
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          color="success"
                          size="medium"
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "var(--primary)",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "var(--primary)",
                              },
                          }}
                          id="cv-permission-switch"
                        />
                      )}
                    />
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-gray-800">
                        Negativação
                      </span>
                      <span className="text-xs text-gray-500">
                        Conceder permissão para negativar devedores neste
                        cliente.
                      </span>
                    </div>
                  </label>

                  <div className="flex items-center gap-3">
                    <CurrencyInput
                      register={register}
                      name="company.priceCV"
                      className="w-36"
                      label="Preço"
                      control={control}
                      disabled={!watch("company.hasCV")}
                    />
                  </div>
                </div>
              </div>
            )}

            <Accordion>
              {categories?.map((category, idxCat) => (
                <Accordion.Panel key={idxCat}>
                  <Accordion.Title>{category.name}</Accordion.Title>
                  <Accordion.Content>
                    {!category?.Queries?.length ? (
                      <div className="text-sm text-center text-gray-500">
                        Não há consultas nesta categoria.
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Principais */}
                        <div>
                          <div className="text-xs font-semibold text-gray-600 mb-2">
                            Consultas principais
                          </div>
                          {category.Queries.some((q) => !q.isAdditional) ? (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                              {category.Queries.map((query, index) => {
                                if (query.isAdditional) return null;

                                const basePrice = getBasePrice
                                  ? getBasePrice(query, tableQueries)
                                  : tableQueries?.find(
                                      (tq) => tq.QueryId === query.id
                                    )?.price ?? query.price;

                                const isRepresentative = isRep
                                  ? isRep(user)
                                  : String(user?.Company?.companyTypeId || "")
                                      .toUpperCase()
                                      .includes("REPRESENT");

                                return (
                                  <QueryRow
                                    key={`main-${idxCat}-${index}`}
                                    idxCat={idxCat}
                                    index={index}
                                    query={query}
                                    register={register}
                                    control={control}
                                    basePrice={basePrice}
                                    isRepresentative={isRepresentative}
                                  />
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">
                              Esta categoria não possui consultas principais.
                            </div>
                          )}
                        </div>

                        {/* Adicionais */}
                        <div>
                          <div className="text-xs font-semibold text-gray-600 mb-2">
                            Consultas adicionais
                          </div>
                          {category.Queries.some((q) => !!q.isAdditional) ? (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                              {category.Queries.map((query, index) => {
                                if (!query.isAdditional) return null;

                                const basePrice = getBasePrice
                                  ? getBasePrice(query, tableQueries)
                                  : tableQueries?.find(
                                      (tq) => tq.QueryId === query.id
                                    )?.price ?? query.price;

                                const isRepresentative = isRep
                                  ? isRep(user)
                                  : String(user?.Company?.companyTypeId || "")
                                      .toUpperCase()
                                      .includes("REPRESENT");

                                return (
                                  <QueryRow
                                    key={`add-${idxCat}-${index}`}
                                    idxCat={idxCat}
                                    index={index}
                                    query={query}
                                    register={register}
                                    control={control}
                                    basePrice={basePrice}
                                    isRepresentative={isRepresentative}
                                  />
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">
                              Esta categoria não possui consultas adicionais.
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </Accordion.Content>
                </Accordion.Panel>
              ))}
            </Accordion>
          </SectionCard>

          <div className="flex justify-center gap-2">
            <Button
              onClick={() => setActiveStep(2)}
              color="gray"
              className="md:max-w-60"
            >
              Voltar
            </Button>
            <Button onClick={handleQueriesData} className="md:max-w-60">
              Próximo
            </Button>
          </div>
        </form>
      )}

      {/* STEP 4: Confirmação */}
      {activeStep === 4 && (
        <form onSubmit={handleSubmit(handleFinishSubmit)} className="space-y-6">
          <SectionCard title="Revisão — cliente">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isCPF ? (
                <ReviewRow label="Nome" value={watch("company.name")} />
              ) : (
                <ReviewRow
                  label="Razão Social"
                  value={watch("company.socialReason")}
                />
              )}
              <ReviewRow label="E-mail" value={watch("company.email")} />
              <ReviewRow label="Celular" value={watch("company.phoneNumber")} />
              {!isCPF && (
                <>
                  <ReviewRow
                    label="Responsável"
                    value={watch("company.responsibleName")}
                  />
                  <ReviewRow
                    label="CPF do responsável"
                    value={watch("company.responsibleCPF")}
                  />
                </>
              )}
              <ReviewRow label="CEP" value={watch("company.cep")} />
              <ReviewRow label="Endereço" value={watch("company.address")} />
              <ReviewRow
                label="Número"
                value={watch("company.addressNumber")}
              />
              <ReviewRow
                label="Complemento"
                value={watch("company.addressComplement")}
              />
              <ReviewRow
                label="Bairro"
                value={watch("company.addressNeighborhood")}
              />
              <ReviewRow label="Cidade" value={watch("company.addressCity")} />
              <ReviewRow
                label="Tipo de pagamento"
                value={watch("company.typePayment")}
              />
              {watch("company.vcm") && (
                <ReviewRow
                  label="VCM"
                  value={formatToReal(watch("company.vcm"))}
                />
              )}
              {/* Revisão dos novos campos */}
              <ReviewRow
                label="Valor máximo de uso"
                value={
                  watch("company.limitUse") !== "" &&
                  watch("company.limitUse") != null
                    ? String(watch("company.limitUse"))
                    : "—"
                }
              />
              <ReviewRow
                label="Término do contrato"
                value={watch("company.dateEndContract")}
              />
            </div>
          </SectionCard>

          <SectionCard title="Revisão — usuário">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ReviewRow label="Nome" value={watch("customer.name")} />
              <ReviewRow label="E-mail" value={watch("customer.email")} />
              <ReviewRow
                label="Celular"
                value={watch("customer.phoneNumber")}
              />
            </div>
          </SectionCard>

          <div className="flex justify-center gap-2">
            <Button
              onClick={() => setActiveStep(3)}
              color="gray"
              className="md:max-w-60"
            >
              Voltar
            </Button>
            <Button
              type="submit"
              className="md:max-w-60"
              isProcessing={isSubmitting}
            >
              Finalizar
            </Button>
          </div>

          {errorQuery && (
            <Alert color="failure" className="mt-3">
              Erro ao tentar cadastrar o cliente!
            </Alert>
          )}
        </form>
      )}

      {/* STEP 5: Sucesso */}
      {activeStep === 5 && (
        <div className="relative overflow-hidden rounded-2xl border bg-white shadow-md">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-emerald-100 opacity-70" />
          <svg
            className="pointer-events-none absolute -top-6 -left-6 w-28 h-28 opacity-50"
            viewBox="0 0 100 100"
            fill="none"
          >
            <circle cx="20" cy="20" r="3" fill="#10B981" />
            <rect x="60" y="10" width="6" height="6" rx="1" fill="#34D399" />
            <circle cx="85" cy="30" r="2.5" fill="#A7F3D0" />
            <rect x="12" y="70" width="5" height="5" rx="1" fill="#6EE7B7" />
            <circle cx="42" cy="85" r="2" fill="#10B981" />
          </svg>
          <svg
            className="pointer-events-none absolute -bottom-6 -right-6 w-28 h-28 opacity-50"
            viewBox="0 0 100 100"
            fill="none"
          >
            <circle cx="80" cy="80" r="3" fill="#10B981" />
            <rect x="30" y="84" width="6" height="6" rx="1" fill="#34D399" />
            <circle cx="18" cy="62" r="2.5" fill="#A7F3D0" />
            <rect x="82" y="22" width="5" height="5" rx="1" fill="#6EE7B7" />
            <circle cx="58" cy="12" r="2" fill="#10B981" />
          </svg>

          <div className="relative p-8 flex flex-col items-center text-center">
            <div className="mb-4 inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100">
              <FaCheckCircle className="w-12 h-12 text-emerald-600" />
            </div>

            <h3 className="text-2xl font-bold text-gray-900">
              Cliente cadastrado com sucesso!
            </h3>
            <p className="text-sm text-gray-600 mt-2 max-w-xl">
              Tudo certo por aqui. Você já pode gerenciar permissões, convidar
              usuários ou cadastrar outro cliente.
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-2xl text-left">
              <div className="rounded-xl border bg-white/70 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">
                  Cliente
                </div>
                <div className="font-medium text-gray-900">
                  {watch("company.typeCustomer") === "CPF"
                    ? watch("company.name")
                    : watch("company.socialReason")}
                </div>
              </div>
              <div className="rounded-xl border bg-white/70 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">
                  E-mail
                </div>
                <div className="font-medium text-gray-900 break-words">
                  {watch("company.email")}
                </div>
              </div>
              <div className="rounded-xl border bg-white/70 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">
                  Celular
                </div>
                <div className="font-medium text-gray-900">
                  {watch("company.phoneNumber")}
                </div>
              </div>
              <div className="rounded-xl border bg-white/70 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">
                  Tipo de pagamento
                </div>
                <div className="font-medium text-gray-900">
                  {watch("company.typePayment")}
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                className="md:max-w-60"
                onClick={() => {
                  reset(undefined, { keepValues: false });
                  setActiveStep(1);
                }}
              >
                Cadastrar outro
              </Button>
              <Button
                color="light"
                className="md:max-w-60"
                onClick={() => {
                  navigate("/admin/customers");
                }}
              >
                Ir para a lista
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* helper para revisão */
const ReviewRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b last:border-b-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-medium text-gray-900 text-right break-all">
      {value || "—"}
    </span>
  </div>
);

export default AdminCustomerForm;
